import { normalizeUnusedInvoiceLine } from "../../shared/invoice-line";
import { InvoiceInputError, type CalculatedInvoiceLine, type DiscountInput, type InvoiceLineInput, type InvoiceTaxRate } from "./types";
import { DocumentTaxError, parseDocumentTaxes, resolveLineTaxes, type DocumentTaxes } from "../../shared/document-taxes";

const MAX_TOTAL = 99_999_999_999n;

function halfUp(numerator: bigint, divisor: bigint): bigint {
  return numerator < 0n ? -((-numerator + divisor / 2n) / divisor) : (numerator + divisor / 2n) / divisor;
}

function safeNumber(value: bigint): number {
  if (value > MAX_TOTAL || value < -MAX_TOTAL) throw new InvoiceInputError("Invoice total exceeds supported bounds");
  return Number(value);
}

export function calculateInvoice(lines: InvoiceLineInput[], discount: DiscountInput | null, rates: Map<string, InvoiceTaxRate>, defaultTaxRateId: string | null = null, documentTaxes?: DocumentTaxes) {
  lines = lines.map(normalizeUnusedInvoiceLine);
  let taxes: DocumentTaxes | undefined;
  try { taxes = documentTaxes ? parseDocumentTaxes(documentTaxes) : undefined; }
  catch (error) { if (error instanceof DocumentTaxError) throw new InvoiceInputError(error.message); throw error; }
  const selected = lines.map(line => {
    try { return taxes ? resolveLineTaxes(taxes, line.taxMode!, line.taxSet ?? []) : []; }
    catch (error) { if (error instanceof DocumentTaxError) throw new InvoiceInputError(error.message); throw error; }
  });
  const effectiveTaxId = (line: InvoiceLineInput) => (line.taxMode ?? (line.taxRateId ? "override" : "none")) === "inherit" ? defaultTaxRateId : (line.taxMode === "none" ? null : line.taxRateId);
  const subtotals = lines.map((line) => {
    if (line.quantityThousandths === null || line.unitAmountMinor === null) return 0n;
    return halfUp(BigInt(line.quantityThousandths) * BigInt(line.unitAmountMinor), 1000n);
  });
  const subtotal = subtotals.reduce((sum, value) => sum + value, 0n);
  if (subtotal < 0n) throw new InvoiceInputError("Invoice total cannot be negative");
  if (subtotals.some((value) => value < 0n) && (discount || lines.some((line, index) => line.unitAmountMinor !== null && line.unitAmountMinor < 0 && (taxes ? selected[index]!.length : effectiveTaxId(line))))) {
    throw new InvoiceInputError("Credits cannot be combined with discounts or line taxes");
  }
  let discountTotal = 0n;
  if (discount?.kind === "percentage") discountTotal = halfUp(subtotal * BigInt(discount.basisPoints), 10_000n);
  if (discount?.kind === "fixed_amount") discountTotal = BigInt(discount.amountMinor);
  if (discountTotal > subtotal) throw new InvoiceInputError("Fixed discount exceeds subtotal");

  const allocations = subtotals.map(() => 0n);
  if (subtotal > 0n && discountTotal > 0n) {
    const ranked = subtotals.map((lineSubtotal, position) => {
      const numerator = discountTotal * lineSubtotal;
      allocations[position] = numerator / subtotal;
      return { position, remainder: numerator % subtotal };
    }).sort((a, b) => a.remainder === b.remainder ? a.position - b.position : a.remainder > b.remainder ? -1 : 1);
    let remaining = discountTotal - allocations.reduce((sum, value) => sum + value, 0n);
    for (const entry of ranked) {
      if (remaining === 0n) break;
      allocations[entry.position] = (allocations[entry.position] ?? 0n) + 1n;
      remaining -= 1n;
    }
  }

  let taxTotal = 0n;
  const calculatedLines: CalculatedInvoiceLine[] = lines.map((line, position) => {
    if (taxes) {
      const taxableBase = selected[position]!.length ? subtotals[position]! - allocations[position]! : 0n;
      const taxBreakdown = selected[position]!.map(definition => ({
        definition: { ...definition }, taxableBaseMinor: safeNumber(taxableBase),
        amountMinor: safeNumber(halfUp(taxableBase * BigInt(definition.rateBasisPoints), 10_000n)),
      }));
      const tax = taxBreakdown.reduce((sum, entry) => sum + BigInt(entry.amountMinor), 0n);
      taxTotal += tax;
      return { ...line, position, taxRateId: null, taxRate: null, taxSet: [...(line.taxSet ?? [])], taxBreakdown,
        lineSubtotalMinor: safeNumber(subtotals[position]!), discountAllocationMinor: safeNumber(allocations[position]!),
        taxableBaseMinor: safeNumber(taxableBase), taxAmountMinor: safeNumber(tax) };
    }
    const selectedTaxId = effectiveTaxId(line);
    const taxRate = selectedTaxId ? rates.get(selectedTaxId) ?? null : null;
    if (selectedTaxId && !taxRate) throw new InvoiceNotFoundForCalculation();
    const taxableBase = taxRate ? subtotals[position]! - allocations[position]! : 0n;
    const tax = taxRate ? halfUp(taxableBase * BigInt(taxRate.rateBasisPoints), 10_000n) : 0n;
    taxTotal += tax;
    return {
      ...line,
      position,
      lineSubtotalMinor: safeNumber(subtotals[position]!),
      discountAllocationMinor: safeNumber(allocations[position]!),
      taxableBaseMinor: safeNumber(taxableBase),
      taxAmountMinor: safeNumber(tax),
      taxRate,
    };
  });
  return {
    ...(taxes ? { taxModelVersion: 1 as const, taxes } : {}),
    lines: calculatedLines,
    subtotalMinor: safeNumber(subtotal),
    discountTotalMinor: safeNumber(discountTotal),
    taxTotalMinor: safeNumber(taxTotal),
    grandTotalMinor: safeNumber(subtotal - discountTotal + taxTotal),
  };
}

export class InvoiceNotFoundForCalculation extends Error {}
